/**
 * An object mapping special characters to their HTML escape codes.
 */
const escapeChars: { [key: string]: string } = {
  "¢": "cent",
  "£": "pound",
  "¥": "yen",
  "€": "euro",
  "©": "copy",
  "®": "reg",
  "<": "lt",
  ">": "gt",
  '"': "quot",
  "&": "amp",
  "'": "#39",
}

/**
 * A string representing a character set for a regular expression.
 * This string is built from the keys of the `escapeChars` object.
 */
let regexString: string = "["
for (const key in escapeChars) {
  regexString += key
}
regexString += "]"

/**
 * A regular expression that matches any of the characters in `escapeChars`.
 */
const regex: RegExp = new RegExp(regexString, "g")

/**
 * Escapes special HTML characters in a given string by replacing them with their corresponding HTML entities.
 *
 *
 * @example
 * escapeHTML('5 < 10 & 20 > 15');
 * // Returns '5 &lt; 10 &amp; 20 &gt; 15'
 */
export function escapeHTML(str: string): string {
  return str.replace(regex, function (m) {
    return "&" + escapeChars[m] + ";"
  })
}

/**
 * Escapes special HTML characters while preserving formatting tags.
 * Preserves: <b>, </b>, <i>, </i>, <strong>, </strong>, <em>, </em>, <u>, </u>
 * Used when loading questionnaire content into the TipTap editor.
 *
 * @example
 * escapeHTMLPreserveFormatting('Q test: <b>bold</b> text');
 * // Returns 'Q test: <b>bold</b> text'
 *
 * escapeHTMLPreserveFormatting('5 < 10 & <i>italic</i>');
 * // Returns '5 &lt; 10 &amp; <i>italic</i>'
 *
 * escapeHTMLPreserveFormatting('Text with <strong>bold</strong> and <em>italic</em>');
 * // Returns 'Text with <strong>bold</strong> and <em>italic</em>'
 */
export function escapeHTMLPreserveFormatting(str: string): string {
  // Placeholder tokens for formatting tags (using Unicode private use area)
  const BOLD_OPEN = "\uE001"
  const BOLD_CLOSE = "\uE002"
  const ITALIC_OPEN = "\uE003"
  const ITALIC_CLOSE = "\uE004"
  const STRONG_OPEN = "\uE005"
  const STRONG_CLOSE = "\uE006"
  const EM_OPEN = "\uE007"
  const EM_CLOSE = "\uE008"
  const UNDERLINE_OPEN = "\uE009"
  const UNDERLINE_CLOSE = "\uE00A"

  // Step 1: Replace formatting tags with placeholders (case-insensitive)
  let result = str
    .replace(/<b>/gi, BOLD_OPEN)
    .replace(/<\/b>/gi, BOLD_CLOSE)
    .replace(/<i>/gi, ITALIC_OPEN)
    .replace(/<\/i>/gi, ITALIC_CLOSE)
    .replace(/<strong>/gi, STRONG_OPEN)
    .replace(/<\/strong>/gi, STRONG_CLOSE)
    .replace(/<em>/gi, EM_OPEN)
    .replace(/<\/em>/gi, EM_CLOSE)
    .replace(/<u>/gi, UNDERLINE_OPEN)
    .replace(/<\/u>/gi, UNDERLINE_CLOSE)

  // Step 2: Escape remaining HTML characters
  result = result.replace(regex, function (m) {
    return "&" + escapeChars[m] + ";"
  })

  // Step 3: Restore formatting tags
  result = result
    .replace(new RegExp(BOLD_OPEN, "g"), "<b>")
    .replace(new RegExp(BOLD_CLOSE, "g"), "</b>")
    .replace(new RegExp(ITALIC_OPEN, "g"), "<i>")
    .replace(new RegExp(ITALIC_CLOSE, "g"), "</i>")
    .replace(new RegExp(STRONG_OPEN, "g"), "<strong>")
    .replace(new RegExp(STRONG_CLOSE, "g"), "</strong>")
    .replace(new RegExp(EM_OPEN, "g"), "<em>")
    .replace(new RegExp(EM_CLOSE, "g"), "</em>")
    .replace(new RegExp(UNDERLINE_OPEN, "g"), "<u>")
    .replace(new RegExp(UNDERLINE_CLOSE, "g"), "</u>")

  return result
}
